import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Chip,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import CellTowerRoundedIcon from '@mui/icons-material/CellTowerRounded';
import RouterRoundedIcon from '@mui/icons-material/RouterRounded';
import WifiTetheringRoundedIcon from '@mui/icons-material/WifiTetheringRounded';
import { gatewaysApi } from '../../api/gateways';
import { manualApi } from '../../api/manual';
import ObjectsMapView from './ObjectsMapView';

const hasCoords = (item) => {
  if (item?.latitude == null || item?.longitude == null) return false;
  const latitude = Number(item.latitude);
  const longitude = Number(item.longitude);
  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    Math.abs(latitude) <= 90 &&
    Math.abs(longitude) <= 180 &&
    !(latitude === 0 && longitude === 0)
  );
};

export default function ObjectsMapPanel({
  complexes = [],
  loading = false,
  settlement = false,
  onOpenBuilding,
  onOpenGateway,
}) {
  const [selectedId, setSelectedId] = useState(null);
  const [gateways, setGateways] = useState([]);
  const [gatewaysLoading, setGatewaysLoading] = useState(true);
  const [gatewaysError, setGatewaysError] = useState('');
  const [buildingProgress, setBuildingProgress] = useState({});
  const [progressLoading, setProgressLoading] = useState(true);
  const [progressError, setProgressError] = useState('');
  const [coverage, setCoverage] = useState(null);
  const [coverageError, setCoverageError] = useState('');
  const [visibleLayers, setVisibleLayers] = useState(() => [
    'building',
    'uspd',
    'base_station',
    ...(settlement ? ['coverage'] : []),
  ]);

  useEffect(() => {
    setVisibleLayers((current) => {
      if (settlement && !current.includes('coverage')) return [...current, 'coverage'];
      if (!settlement && current.includes('coverage')) {
        return current.filter((item) => item !== 'coverage');
      }
      return current;
    });
  }, [settlement]);

  useEffect(() => {
    let active = true;
    setGatewaysLoading(true);
    gatewaysApi
      .list({ skip: 0, limit: 2000, map_only: true })
      .then((items) => {
        if (!active) return;
        setGateways(Array.isArray(items) ? items : []);
        setGatewaysError('');
      })
      .catch(() => {
        if (!active) return;
        setGateways([]);
        setGatewaysError('Не удалось загрузить шлюзы для карты');
      })
      .finally(() => {
        if (active) setGatewaysLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    if (!settlement) {
      setCoverage(null);
      setCoverageError('');
      return () => {
        active = false;
      };
    }
    gatewaysApi
      .getLoRaWanMapCoverage({ hours: 720 })
      .then((response) => {
        if (!active) return;
        setCoverage(response);
        setCoverageError('');
      })
      .catch(() => {
        if (!active) return;
        setCoverage(null);
        setCoverageError('Не удалось загрузить покрытие LoRaWAN');
      });
    return () => {
      active = false;
    };
  }, [settlement]);

  useEffect(() => {
    let active = true;
    const now = new Date();
    const year = now.getFullYear();
    const month = `${year}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    setProgressLoading(true);
    manualApi
      .getSubmissionProgressSummary({ year, month, source: 'ANY', current_only: true })
      .then((response) => {
        if (!active) return;
        const byBuilding = {};
        (response?.buildings || []).forEach((item) => {
          byBuilding[String(item.building_id)] = item.current_month;
        });
        setBuildingProgress(byBuilding);
        setProgressError('');
      })
      .catch(() => {
        if (!active) return;
        setBuildingProgress({});
        setProgressError('Не удалось загрузить состояние показаний по домам');
      })
      .finally(() => {
        if (active) setProgressLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const { markers, withoutCoords, gatewaysWithoutCoords, layerCounts } = useMemo(() => {
    const nextMarkers = [];
    let missing = 0;
    let missingGateways = 0;
    const coverageLinks = Array.isArray(coverage?.links) ? coverage.links : [];
    const coverageByBuilding = new Map();
    const coverageByGateway = new Map();
    const coverageGatewayCoords = new Map();
    coverageLinks.forEach((link) => {
      const buildingId = String(link.building_id);
      const gatewayId = String(link.gateway_id);
      if (!coverageByBuilding.has(buildingId)) coverageByBuilding.set(buildingId, []);
      if (!coverageByGateway.has(gatewayId)) coverageByGateway.set(gatewayId, []);
      coverageByBuilding.get(buildingId).push(link);
      coverageByGateway.get(gatewayId).push(link);
      if (
        link.gateway_latitude != null &&
        link.gateway_longitude != null &&
        Number.isFinite(Number(link.gateway_latitude)) &&
        Number.isFinite(Number(link.gateway_longitude))
      ) {
        coverageGatewayCoords.set(gatewayId, {
          latitude: Number(link.gateway_latitude),
          longitude: Number(link.gateway_longitude),
        });
      }
    });

    (complexes || []).forEach((complex) => {
      const buildings = Array.isArray(complex?.buildings) ? complex.buildings : [];
      buildings.forEach((building) => {
        if (!hasCoords(building)) {
          missing += 1;
          return;
        }
        const counts = building.device_counts || {};
        const progress = buildingProgress[String(building.id)];
        const totalDevices = Number(progress?.total_devices ?? building.device_count ?? counts.total ?? 0);
        const submittedDevices = Number(progress?.submitted_devices ?? 0);
        const totalApartments = Math.max(
          Number(progress?.total_apartments ?? 0) - Number(progress?.no_devices_apartments ?? 0),
          0
        );
        const completedApartments = Number(progress?.completed_apartments ?? 0);
        let dataState = 'unknown';
        if (progress) {
          if (progress.availability === 'before_start') dataState = 'before_start';
          else if (totalDevices <= 0) dataState = 'no_devices';
          else if (submittedDevices <= 0) dataState = 'no_data';
          else if (submittedDevices >= totalDevices) dataState = 'complete';
          else dataState = 'partial';
        }
        nextMarkers.push({
          id: `building:${building.id}`,
          kind: 'building',
          title: building.name || 'Дом',
          subtitle: complex.name || null,
          address: building.address || null,
          latitude: Number(building.latitude),
          longitude: Number(building.longitude),
          complexId: complex.id,
          buildingId: building.id,
          deviceCount: totalDevices,
          dataState,
          submittedDevices,
          readingProgress: totalDevices > 0 ? (submittedDevices / totalDevices) * 100 : null,
          totalApartments,
          completedApartments,
          apartmentProgress:
            totalApartments > 0 ? (completedApartments / totalApartments) * 100 : null,
          coverageGateways: coverageByBuilding.get(String(building.id))?.length || 0,
          periodStart: progress?.period_start || null,
          periodEnd: progress?.period_end || null,
          stats: {
            total: totalDevices,
            hvs: Number(counts.hvs ?? 0),
            gvs: Number(counts.gvs ?? 0),
            heating: Number(counts.heating ?? 0),
            electricity: Number(counts.electricity ?? 0),
            heat_allocators: Number(counts.heat_allocators ?? 0),
          },
        });
      });
    });

    (gateways || []).forEach((gateway) => {
      const observedCoords = coverageGatewayCoords.get(String(gateway.id));
      const coordinateSource = hasCoords(gateway) ? 'configured' : observedCoords ? 'lorawan' : null;
      const coordinates = coordinateSource === 'configured' ? gateway : observedCoords;
      if (!hasCoords(coordinates)) {
        missingGateways += 1;
        return;
      }
      const isBaseStation = String(gateway.gateway_type || '').toUpperCase() === 'BS';
      nextMarkers.push({
        id: `gateway:${gateway.id}`,
        kind: isBaseStation ? 'base_station' : 'uspd',
        title: gateway.name || (isBaseStation ? 'Базовая станция' : 'УСПД'),
        subtitle: isBaseStation ? 'Базовая станция LoRaWAN' : 'УСПД',
        address: gateway.address || gateway.location || null,
        latitude: Number(coordinates.latitude),
        longitude: Number(coordinates.longitude),
        gatewayId: gateway.id,
        gatewayType: gateway.gateway_type,
        status: gateway.status,
        host: gateway.host,
        lastSeenAt: gateway.last_seen_at || gateway.lorawan_last_seen_at,
        lorawanGatewayId: gateway.lorawan_gateway_id || gateway.mac_address,
        devicesTotal: Number(gateway.devices_total || 0),
        devicesProblem: Number(gateway.devices_problem || 0),
        coverageBuildings: coverageByGateway.get(String(gateway.id))?.length || 0,
        coordinateSource,
      });
    });

    const counts = nextMarkers.reduce(
      (acc, marker) => ({ ...acc, [marker.kind]: (acc[marker.kind] || 0) + 1 }),
      { building: 0, uspd: 0, base_station: 0 }
    );
    return {
      markers: nextMarkers,
      withoutCoords: missing,
      gatewaysWithoutCoords: missingGateways,
      layerCounts: counts,
    };
  }, [buildingProgress, complexes, coverage, gateways]);

  const dataCounts = useMemo(
    () =>
      markers.reduce(
        (acc, marker) => {
          if (marker.kind === 'building' && Object.prototype.hasOwnProperty.call(acc, marker.dataState)) {
            acc[marker.dataState] += 1;
          }
          return acc;
        },
        { complete: 0, partial: 0, no_data: 0, no_devices: 0, before_start: 0, unknown: 0 }
      ),
    [markers]
  );

  const visibleMarkers = useMemo(
    () => markers.filter((marker) => visibleLayers.includes(marker.kind)),
    [markers, visibleLayers]
  );

  const handleSelect = (marker) => {
    setSelectedId(marker?.id || null);
  };

  const handleOpen = (marker) => {
    setSelectedId(marker.id);
    if (marker?.kind === 'building') {
      if (marker.buildingId && onOpenBuilding) {
        onOpenBuilding(marker.complexId, marker.buildingId);
      }
      return;
    }
    if (marker?.gatewayId && onOpenGateway) {
      onOpenGateway(marker.gatewayId);
    }
  };

  const layers = [
    { id: 'building', label: 'Дома', Icon: ApartmentRoundedIcon },
    { id: 'uspd', label: 'УСПД', Icon: RouterRoundedIcon },
    { id: 'base_station', label: 'Базовые станции', Icon: CellTowerRoundedIcon },
    ...(settlement
      ? [{ id: 'coverage', label: 'Покрытие', Icon: WifiTetheringRoundedIcon }]
      : []),
  ];

  return (
    <Stack spacing={1.5}>
      <Paper
        sx={{
          p: 2,
          borderRadius: 3,
          border: '1px solid var(--border-color)',
          background: 'var(--surface-bg)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Карта объектов
          </Typography>
          <ToggleButtonGroup
            value={visibleLayers}
            onChange={(_event, value) => {
              setVisibleLayers(value);
              setSelectedId(null);
            }}
            size="small"
            aria-label="Слои карты"
            sx={{ flexWrap: 'wrap', gap: 0.75, '& .MuiToggleButtonGroup-grouped': { m: 0 } }}
          >
            {layers.map(({ id, label, Icon }) => (
              <ToggleButton
                key={id}
                value={id}
                sx={{ px: 1.25, py: 0.55, borderRadius: '10px !important', textTransform: 'none' }}
              >
                <Icon sx={{ mr: 0.65, fontSize: 18 }} />
                {label}
                {id !== 'coverage' ? ` · ${layerCounts[id] || 0}` : ''}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
        {!progressLoading && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
            <Chip size="small" label={`Собрано · ${dataCounts.complete}`} sx={{ color: '#15803D', background: '#DCFCE7' }} />
            <Chip size="small" label={`Частично · ${dataCounts.partial}`} sx={{ color: '#B45309', background: '#FEF3C7' }} />
            <Chip size="small" label={`Нет показаний · ${dataCounts.no_data}`} sx={{ color: '#B91C1C', background: '#FEE2E2' }} />
            {dataCounts.no_devices > 0 && (
              <Chip size="small" label={`Без приборов · ${dataCounts.no_devices}`} sx={{ color: '#475569', background: '#E2E8F0' }} />
            )}
            {dataCounts.before_start > 0 && (
              <Chip size="small" label={`Учёт не начат · ${dataCounts.before_start}`} sx={{ color: '#64748B', background: '#F1F5F9' }} />
            )}
          </Stack>
        )}
        {settlement && coverage?.links?.length > 0 && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            <Chip size="small" label="Покрытие за 30 дней" variant="outlined" />
            <Chip
              size="small"
              label={`С покрытием: ${coverage.buildings_covered} из ${coverage.buildings_with_devices}`}
              sx={{ color: '#047857', background: '#ECFDF5' }}
            />
            <Chip
              size="small"
              label={`Активные БС: ${coverage.gateways_active}`}
              sx={{ color: '#1D4ED8', background: '#EFF6FF' }}
            />
          </Stack>
        )}
        {settlement && coverage && !coverage.links?.length && (
          <Chip
            size="small"
            label="Покрытие: нет данных за 30 дней"
            variant="outlined"
            sx={{ mt: 1, color: '#64748B', borderColor: '#CBD5E1' }}
          />
        )}
      </Paper>

      {loading || gatewaysLoading || progressLoading ? (
        <Alert severity="info">Загружаем объекты…</Alert>
      ) : (
        <ObjectsMapView
          markers={visibleMarkers}
          selectedId={selectedId}
          onSelect={handleSelect}
          onOpen={handleOpen}
          coverageLinks={visibleLayers.includes('coverage') ? coverage?.links || [] : []}
          height={560}
        />
      )}

      {!gatewaysLoading && gatewaysError && <Alert severity="warning">{gatewaysError}</Alert>}
      {!progressLoading && progressError && <Alert severity="warning">{progressError}</Alert>}
      {settlement && coverageError && <Alert severity="warning">{coverageError}</Alert>}

      {!loading && withoutCoords > 0 && (
        <Alert severity="info">
          У {withoutCoords} домов ещё нет координат. Откройте карточку дома и выберите адрес из
          подсказок — точка появится на карте.
        </Alert>
      )}
      {!gatewaysLoading && gatewaysWithoutCoords > 0 && (
        <Alert severity="info">
          Без координат: {gatewaysWithoutCoords} УСПД или базовых станций.
        </Alert>
      )}
    </Stack>
  );
}
